//
// # ManageLinkagesDialog
//
// Modal dialog for viewing, adding, and removing design linkages.
// Adapts its content based on the active design's content kind:
//   Symbol    -> lists linked schematics
//   Schematic -> shows linked symbol + lists linked layouts
//   Layout    -> shows linked schematic
//

import React, { useCallback, useMemo, useState } from "react";

import { Ident } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";

import { Modal } from "./Modal/Modal";
import { useWorkspace, useWorkspaceSnapshot } from "../context";
import type { IdentWithName, ManageLinkagesDialogState } from "../types";

// Sentinel values for the pdkId filter dropdown.
const ALL_PDKS = "__all__";
const NO_PDK = "__none__";

// Validation: path segments must be alphanumeric, underscore, or hyphen.
const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

function isValidPath(path: string): boolean {
  const segments = path.split("/").filter((s) => s.length > 0);
  return segments.length > 0 && segments.every((s) => NAME_PATTERN.test(s));
}

// Shared input/button styles matching the IDE design system.
const inputClass =
  "w-full rounded border px-2.5 py-1.5 text-xs font-mono outline-none";
const labelClass =
  "block text-[10px] font-semibold uppercase tracking-widest mb-1";

// A single linkage entry with go-to and unlink actions.
function LinkageEntry({
  entry,
  onGoTo,
  onUnlink,
}: {
  readonly entry: IdentWithName;
  readonly onGoTo: (ident: schema.Ident) => void;
  readonly onUnlink: (ident: schema.Ident) => void;
}) {
  return (
    <div
      className="flex items-center justify-between rounded px-2 py-1 text-xs"
      style={{ backgroundColor: "var(--color-ide-bg)" }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          className="font-mono hover:underline text-left truncate"
          style={{ color: "var(--color-ide-accent)" }}
          title={`Open ${entry.displayName}`}
          onClick={() => onGoTo(entry.ident)}
        >
          {entry.displayName}
        </button>
        {entry.pdkId && (
          <span
            className="text-[9px] px-1 py-0.5 rounded shrink-0"
            style={{
              backgroundColor: "var(--color-ide-surface-2)",
              color: "var(--color-ide-text-dim)",
            }}
          >
            PDK: {entry.pdkId}
          </span>
        )}
      </div>
      <button
        className="ml-2 px-1.5 py-0.5 rounded text-[10px] border shrink-0"
        style={{
          borderColor: "var(--color-ide-error, #e53e3e)",
          color: "var(--color-ide-error, #e53e3e)",
        }}
        onClick={() => onUnlink(entry.ident)}
      >
        Unlink
      </button>
    </div>
  );
}

// Section header + list of linkage entries, with pdkId filter and inline add form.
function LinkageSection({
  title,
  entries,
  singleValue,
  defaultPdkFilter,
  onGoTo,
  onUnlink,
  onAdd,
  placeholder,
}: {
  readonly title: string;
  readonly entries: ReadonlyArray<IdentWithName>;
  readonly singleValue: boolean;
  readonly defaultPdkFilter?: string;
  readonly onGoTo: (ident: schema.Ident) => void;
  readonly onUnlink: (ident: schema.Ident) => void;
  readonly onAdd: (path: string) => Promise<void>;
  readonly placeholder: string;
}) {
  const [path, setPath] = useState("");
  const [adding, setAdding] = useState(false);
  const [pdkFilter, setPdkFilter] = useState<string>(
    defaultPdkFilter ?? ALL_PDKS
  );

  // Collect unique pdkIds from entries for the filter dropdown
  const { pdkIds, hasNoPdk } = useMemo(() => {
    const pdks = new Set<string>();
    let noPdk = false;
    for (const entry of entries) {
      if (entry.pdkId) pdks.add(entry.pdkId);
      else noPdk = true;
    }
    return { pdkIds: Array.from(pdks).sort(), hasNoPdk: noPdk };
  }, [entries]);

  // Filter entries by selected pdkId
  const filteredEntries = useMemo(() => {
    if (pdkFilter === ALL_PDKS) return entries;
    if (pdkFilter === NO_PDK) return entries.filter((e) => !e.pdkId);
    return entries.filter((e) => e.pdkId === pdkFilter || !e.pdkId);
  }, [entries, pdkFilter]);

  const pathValid = path.length === 0 || isValidPath(path);
  const canAdd =
    isValidPath(path) &&
    !adding &&
    // Single-value fields can only be set if currently empty
    (!singleValue || entries.length === 0);

  const handleAdd = useCallback(async () => {
    if (!canAdd) return;
    setAdding(true);
    await onAdd(path);
    setPath("");
    setAdding(false);
  }, [canAdd, path, onAdd]);

  // Only show filter when there are multiple pdkIds and multiple entries
  const showFilter =
    !singleValue && (pdkIds.length > 1 || (pdkIds.length > 0 && hasNoPdk));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label
          className={labelClass}
          style={{ color: "var(--color-ide-text-muted)", marginBottom: 0 }}
        >
          {title}
        </label>
        {showFilter && (
          <select
            value={pdkFilter}
            onChange={(e) => setPdkFilter(e.target.value)}
            className="text-[10px] rounded border px-1.5 py-0.5 outline-none"
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            <option value={ALL_PDKS}>All PDKs</option>
            {pdkIds.map((pdk) => (
              <option key={pdk} value={pdk}>
                {pdk}
              </option>
            ))}
            {hasNoPdk && <option value={NO_PDK}>None (PDK-agnostic)</option>}
          </select>
        )}
      </div>
      <div className="flex flex-col gap-1 mb-2">
        {filteredEntries.length === 0 && (
          <div
            className="text-xs px-2 py-1 italic"
            style={{ color: "var(--color-ide-text-dim)" }}
          >
            {entries.length === 0 ? "None" : "None matching filter"}
          </div>
        )}
        {filteredEntries.map((entry) => (
          <LinkageEntry
            key={Ident.stringify(entry.ident)}
            entry={entry}
            onGoTo={onGoTo}
            onUnlink={onUnlink}
          />
        ))}
      </div>
      {/* Add form -- hidden when single-value field is already set */}
      {(!singleValue || entries.length === 0) && (
        <div className="flex gap-1.5">
          <input
            type="text"
            placeholder={placeholder}
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleAdd();
            }}
            className={inputClass}
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: pathValid
                ? "var(--color-ide-border)"
                : "var(--color-ide-error)",
              color: "var(--color-ide-text)",
            }}
          />
          <button
            onClick={() => void handleAdd()}
            disabled={!canAdd}
            className="px-3 py-1.5 text-xs rounded border shrink-0"
            style={{
              backgroundColor: canAdd
                ? "var(--color-ide-accent)"
                : "var(--color-ide-bg)",
              borderColor: canAdd
                ? "var(--color-ide-accent)"
                : "var(--color-ide-border)",
              color: canAdd ? "#fff" : "var(--color-ide-text-dim)",
              cursor: canAdd ? "pointer" : "not-allowed",
            }}
          >
            {adding ? "..." : "Link"}
          </button>
        </div>
      )}
    </div>
  );
}

export interface ManageLinkagesDialogProps {
  readonly state: ManageLinkagesDialogState;
  readonly onClose: () => void;
}

export function ManageLinkagesDialog({
  state,
  onClose,
}: ManageLinkagesDialogProps) {
  const workspace = useWorkspace();
  const designName = Ident.displayName(state.designIdent);

  const handleGoTo = useCallback(
    (ident: schema.Ident) => {
      void workspace.goToLinkage(ident);
    },
    [workspace]
  );

  const handleUnlink = useCallback(
    (ident: schema.Ident) => {
      void workspace.unlinkDesign(ident);
    },
    [workspace]
  );

  const handleAdd = useCallback(
    async (path: string) => {
      await workspace.linkDesign(path);
    },
    [workspace]
  );

  const linkages = state.linkages;

  return (
    <Modal open={true} onClose={onClose} title="Manage Linkages">
      <div className="flex flex-col gap-3">
        {/* Design being managed (read-only) */}
        <div>
          <label
            className={labelClass}
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Design
          </label>
          <div
            className={inputClass}
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-dim)",
            }}
          >
            {designName}
            {linkages && (
              <span
                className="ml-2 text-[10px]"
                style={{ color: "var(--color-ide-text-dim)" }}
              >
                ({linkages.contentKind})
              </span>
            )}
          </div>
        </div>

        {/* Loading state */}
        {!linkages && !state.error && (
          <div
            className="text-xs text-center py-4"
            style={{ color: "var(--color-ide-text-dim)" }}
          >
            Loading linkages...
          </div>
        )}

        {/* Error banner */}
        {state.error && (
          <div
            className="rounded border px-3 py-2 text-xs"
            style={{
              backgroundColor: "rgba(241, 76, 76, 0.1)",
              borderColor: "var(--color-ide-error)",
              color: "var(--color-ide-error)",
            }}
          >
            {state.error}
          </div>
        )}

        {/* Linkage sections based on content kind */}
        {linkages?.contentKind === "symbol" && (
          <LinkageSection
            title="Linked Schematics"
            entries={linkages.schematicImpls ?? []}
            singleValue={false}
            defaultPdkFilter={linkages.pdkId}
            onGoTo={handleGoTo}
            onUnlink={handleUnlink}
            onAdd={handleAdd}
            placeholder="library/cell/schematic"
          />
        )}

        {linkages?.contentKind === "schematic" && (
          <>
            <LinkageSection
              title="Linked Symbol"
              entries={linkages.symbolImpl ? [linkages.symbolImpl] : []}
              singleValue={true}
              onGoTo={handleGoTo}
              onUnlink={handleUnlink}
              onAdd={handleAdd}
              placeholder="library/cell/symbol"
            />
            <LinkageSection
              title="Linked Layouts"
              entries={linkages.layoutImpls ?? []}
              singleValue={false}
              defaultPdkFilter={linkages.pdkId}
              onGoTo={handleGoTo}
              onUnlink={handleUnlink}
              onAdd={handleAdd}
              placeholder="library/cell/layout"
            />
          </>
        )}

        {linkages?.contentKind === "layout" && (
          <LinkageSection
            title="Linked Schematic"
            entries={linkages.schematicImpl ? [linkages.schematicImpl] : []}
            singleValue={true}
            onGoTo={handleGoTo}
            onUnlink={handleUnlink}
            onAdd={handleAdd}
            placeholder="library/cell/schematic"
          />
        )}

        {/* Close button */}
        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              backgroundColor: "var(--color-ide-bg)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-muted)",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
